// Client-safe export catalog — the single source of truth for WHICH Bid
// Tabulations tables are exportable, in which formats, under which permission,
// and validated by which filter schema. It imports only Zod request schemas (no
// services / DB), so it is safe to import from client components to drive the
// export UI. The server-side `registry.ts` merges these entries with their
// builders, keyed by the same `ExportTableId`, so adding a table here forces a
// matching builder to exist (and vice-versa).

import { z } from 'zod/v4';

import type { ExportFormat } from './export.types';

import {
  getMyBidsQuerySchema,
  getScoreboardQuerySchema,
  getVendorBacklogQuerySchema,
  getVendorBidHistoryQuerySchema,
  getVendorsQuerySchema,
} from '../vendors/vendors.schema';
import {
  exportVendorSimulatedBidsQuerySchema,
  vendorSensitivityQuerySchema,
} from '../vendor-sensitivity/vendor-sensitivity.schema';
import { getBidsQuerySchema } from '../bids/bids.schema';
import {
  getItemHistoryQuerySchema,
  getItemsQuerySchema,
} from '../items/items.schema';
import { getLettingsQuerySchema } from '../lettings/lettings.schema';
import { getProposalsQuerySchema } from '../proposals/proposals.schema';
import { getProposalItemsWithBidItemsQuerySchema } from '../proposal-items/proposal-items.schema';
import {
  ownerFundingComparisonQuerySchema,
  ownerFundingSummaryQuerySchema,
} from '../owner-funding/owner-funding.schema';

/** Permission body (resource → actions). Cast to the API's typed shape at the
 *  route boundary; kept loose here so the domains package stays API-agnostic. */
export type ExportPermission = Record<string, string[]>;

export type ExportTableMeta = {
  /** Allowed output formats; the first is the default. */
  formats: ExportFormat[];
  permission: ExportPermission;
  /** Filename prefix (before the format-derived extension). */
  filename: string;
  /** Validates the request query (filters). Pagination fields are ignored. */
  schema: z.ZodObject;
};

const VENDORS: ExportPermission = { vendors: ['read'] };
const ITEMS: ExportPermission = { items: ['read'] };
const LETTINGS: ExportPermission = { lettings: ['read'] };
const PROPOSALS: ExportPermission = { proposals: ['read'] };
const OWNERS: ExportPermission = { owners: ['read'] };

// A flat export (one table of rows) offers both formats. A *composed* export —
// one whose builder returns several sheets (year/category/geography breakdowns,
// per-year sensitivity, etc.) — is XLSX-only: CSV is inherently flat and can't
// represent tabs, so we don't offer a CSV that would silently flatten or drop
// data. Keep `formats` in lockstep with the builder's sheet count in registry.ts.
const CSV_XLSX: ExportFormat[] = ['csv', 'xlsx'];
const XLSX_ONLY: ExportFormat[] = ['xlsx'];

// Schemas that add the path-scoped id the table needs (vendor / proposal / org).
const orgScopedSchema = z.object({ ownerId: z.string().uuid().optional() });
const proposalScopedSchema = z.object({ proposalId: z.string().uuid() });
const backlogScopedSchema = getVendorBacklogQuerySchema.extend({
  globalVendorId: z.string(),
});
const compositionScopedSchema = getVendorBacklogQuerySchema.extend({
  vendorId: z.string(),
});
const bidHistorySchema = getVendorBidHistoryQuerySchema.extend({
  globalVendorId: z.string().optional(),
});
const csvArray = z.preprocess(
  (v) => (typeof v === 'string' ? v.split(',').filter(Boolean) : v),
  z.array(z.string()).optional(),
);
const globalVendorsSchema = z.object({
  search: z.string().optional(),
  states: csvArray,
  ids: csvArray,
});
// Item history is scoped to one item, supplied as a filter (the route is
// generic /exports/:table, so the item id rides in the query, not the path).
const itemHistoryExportSchema = getItemHistoryQuerySchema.extend({
  itemId: z.string().uuid(),
});

// Active Backlog leaderboard is owner-wide (RLS-scoped) and takes no filters —
// the export re-runs the same read the endpoint does.
const activeBacklogSchema = z.object({});

// Pay Estimate History is scoped to one contract; the proposal id rides in the
// query (the export route is generic /exports/:table).
const payEstimateHistorySchema = z.object({
  proposalId: z.string().uuid(),
});

// Estimate line-item rollup is scoped to one estimate of one contract.
const estimateRollupSchema = z.object({
  proposalId: z.string().uuid(),
  estimateNumber: z.coerce.number().int(),
});

// Item Over/Under-runs is scoped to one catalog item.
const overUnderSchema = z.object({
  itemId: z.string().uuid(),
});

// Vendor-detail pay-estimate tables are scoped to one parent (global vendor).
const globalVendorScopedSchema = z.object({
  globalVendorId: z.string().uuid(),
});

export const EXPORT_TABLES = {
  // ── Cross-state / vendors ────────────────────────────────────────────────
  scoreboard: {
    formats: CSV_XLSX,
    permission: VENDORS,
    filename: 'scoreboard',
    schema: getScoreboardQuerySchema,
  },
  'scoreboard-geography': {
    formats: XLSX_ONLY,
    permission: VENDORS,
    filename: 'scoreboard-geography',
    schema: getScoreboardQuerySchema,
  },
  'scoreboard-category-breakdown': {
    formats: CSV_XLSX,
    permission: VENDORS,
    filename: 'scoreboard-category-breakdown',
    schema: getScoreboardQuerySchema,
  },
  'scoreboard-monthly': {
    formats: CSV_XLSX,
    permission: VENDORS,
    filename: 'scoreboard-monthly',
    schema: getScoreboardQuerySchema,
  },
  vendors: {
    formats: CSV_XLSX,
    permission: VENDORS,
    filename: 'vendors',
    schema: getVendorsQuerySchema,
  },
  'global-vendors': {
    formats: CSV_XLSX,
    permission: VENDORS,
    filename: 'global-vendors',
    schema: globalVendorsSchema,
  },
  'active-backlog': {
    formats: CSV_XLSX,
    permission: PROPOSALS,
    filename: 'active-backlog',
    schema: activeBacklogSchema,
  },
  'pay-estimate-history': {
    formats: CSV_XLSX,
    permission: PROPOSALS,
    filename: 'pay-estimate-history',
    schema: payEstimateHistorySchema,
  },
  'estimate-rollup': {
    formats: CSV_XLSX,
    permission: PROPOSALS,
    filename: 'estimate-rollup',
    schema: estimateRollupSchema,
  },
  'over-under-breakdown': {
    formats: CSV_XLSX,
    permission: ITEMS,
    filename: 'over-under-breakdown',
    schema: overUnderSchema,
  },
  'over-under-contracts': {
    formats: CSV_XLSX,
    permission: ITEMS,
    filename: 'over-under-contracts',
    schema: overUnderSchema,
  },
  'vendor-pay-backlog-by-year': {
    formats: CSV_XLSX,
    permission: VENDORS,
    filename: 'vendor-backlog-by-year',
    schema: globalVendorScopedSchema,
  },
  'vendor-pay-active-projects': {
    formats: CSV_XLSX,
    permission: VENDORS,
    filename: 'vendor-active-projects',
    schema: globalVendorScopedSchema,
  },
  'vendor-pay-throughput': {
    formats: CSV_XLSX,
    permission: VENDORS,
    filename: 'vendor-throughput',
    schema: globalVendorScopedSchema,
  },
  'vendor-bid-history': {
    formats: CSV_XLSX,
    permission: VENDORS,
    filename: 'vendor-bid-history',
    schema: bidHistorySchema,
  },
  'my-bids': {
    formats: CSV_XLSX,
    permission: VENDORS,
    filename: 'my-bids',
    schema: getMyBidsQuerySchema,
  },
  'vendor-backlog': {
    formats: CSV_XLSX,
    permission: VENDORS,
    filename: 'vendor-backlog',
    schema: backlogScopedSchema,
  },
  'vendor-backlog-composition': {
    formats: CSV_XLSX,
    permission: VENDORS,
    filename: 'vendor-backlog-composition',
    schema: compositionScopedSchema,
  },
  'vendor-sensitivity': {
    formats: XLSX_ONLY,
    permission: VENDORS,
    filename: 'vendor-sensitivity',
    schema: vendorSensitivityQuerySchema,
  },
  'vendor-sensitivity-bids': {
    formats: CSV_XLSX,
    permission: VENDORS,
    filename: 'vendor-sensitivity-bids',
    schema: exportVendorSimulatedBidsQuerySchema,
  },

  // ── Owner-scoped reference ───────────────────────────────────────────────
  items: {
    formats: CSV_XLSX,
    permission: ITEMS,
    filename: 'items',
    schema: getItemsQuerySchema,
  },
  categories: {
    formats: CSV_XLSX,
    permission: ITEMS,
    filename: 'categories',
    schema: orgScopedSchema,
  },
  collections: {
    formats: CSV_XLSX,
    permission: ITEMS,
    filename: 'collections',
    schema: orgScopedSchema,
  },
  lettings: {
    formats: CSV_XLSX,
    permission: LETTINGS,
    filename: 'lettings',
    schema: getLettingsQuerySchema,
  },
  'item-history': {
    formats: CSV_XLSX,
    permission: ITEMS,
    filename: 'item-history',
    schema: itemHistoryExportSchema,
  },

  // ── Proposals (within a letting) ─────────────────────────────────────────
  proposals: {
    formats: CSV_XLSX,
    permission: PROPOSALS,
    filename: 'proposals',
    schema: getProposalsQuerySchema,
  },
  'bid-results': {
    formats: CSV_XLSX,
    permission: PROPOSALS,
    filename: 'bid-results',
    schema: getBidsQuerySchema,
  },
  // The 4-sheet bid-tab workbook (Bid Results, Items, Categories, Collections).
  // XLSX-only — a CSV can't carry tabs (see the composed-export note above).
  'bid-tab': {
    formats: XLSX_ONLY,
    permission: PROPOSALS,
    filename: 'bid-tab',
    schema: proposalScopedSchema,
  },
  'proposal-items': {
    formats: CSV_XLSX,
    permission: PROPOSALS,
    filename: 'proposal-items',
    schema: getProposalItemsWithBidItemsQuerySchema,
  },
  'proposal-post-bid-items': {
    formats: CSV_XLSX,
    permission: PROPOSALS,
    filename: 'proposal-post-bid-items',
    schema: proposalScopedSchema,
  },
  'proposal-post-bid-collections': {
    formats: CSV_XLSX,
    permission: PROPOSALS,
    filename: 'proposal-post-bid-collections',
    schema: proposalScopedSchema,
  },

  // ── Funding reports ──────────────────────────────────────────────────────
  'owner-funding-summary': {
    formats: XLSX_ONLY,
    permission: OWNERS,
    filename: 'owner-funding-summary',
    schema: ownerFundingSummaryQuerySchema,
  },
  'owner-funding-comparison': {
    formats: XLSX_ONLY,
    permission: OWNERS,
    filename: 'owner-funding-comparison',
    schema: ownerFundingComparisonQuerySchema,
  },
} satisfies Record<string, ExportTableMeta>;

export type ExportTableId = keyof typeof EXPORT_TABLES;

/** The accepted filter shape for a given table (pre-transform input form). */
export type ExportFilters<T extends ExportTableId> = Partial<
  z.input<(typeof EXPORT_TABLES)[T]['schema']>
>;

export function getExportTableMeta(table: string): ExportTableMeta | undefined {
  return (EXPORT_TABLES as Record<string, ExportTableMeta>)[table];
}

/** Keys never forwarded as export filters (pagination/sort/grouping are
 *  builder-owned — the export always pulls the full set in its own order). */
const NON_FILTER_KEYS = new Set(['limit', 'offset', 'page', 'sort', 'group']);

/**
 * Strip an arbitrary params object down to only the keys this table's schema
 * accepts (minus pagination). Guarantees we never send a filter the export
 * doesn't expect — tied to the same schema the builder validates against.
 */
export function pickExportFilters<T extends ExportTableId>(
  table: T,
  raw: Record<string, unknown>,
): ExportFilters<T> {
  const meta = EXPORT_TABLES[table] as ExportTableMeta;
  const allowed = meta.schema.shape;
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(allowed)) {
    if (NON_FILTER_KEYS.has(key)) {
      continue;
    }
    const value = raw[key];
    if (value === undefined || value === null) {
      continue;
    }
    if (Array.isArray(value) && value.length === 0) {
      continue;
    }
    out[key] = value;
  }
  return out as ExportFilters<T>;
}
